/**
 * Testing Page - Simulates Customer's Website
 *
 * This page acts as a mock customer website that loads and uses our SDKs.
 * It demonstrates how customers will integrate the SDK in their applications.
 */

import { useEffect, useState } from "react";
import { TokenManager } from "../components/TokenManager";
import { SUPPORTED_LOCALES, type SupportedLocale } from "../shared/constants/locales";
import { SUPPORTED_CONSUMERS, getValidConsumer, type SupportedConsumer } from "../shared/constants/consumers";
import { LEGAL_ID_PROVIDERS, type LegalIdProvider } from "../shared/constants/verification";

// Language display names - matches LanguageSelector component
const LANGUAGE_NAMES: Record<string, { name: string; nativeName: string }> = {
  'en': { name: 'English', nativeName: 'English' },
  'ar': { name: 'Arabic', nativeName: 'العربية' },
  'bn': { name: 'Bengali', nativeName: 'বাংলা' },
  'es': { name: 'Spanish', nativeName: 'Español' },
  'fa': { name: 'Persian', nativeName: 'فارسی' },
  'fr-CA': { name: 'French (Canada)', nativeName: 'Français (Canada)' },
  'hi': { name: 'Hindi', nativeName: 'हिन्दी' },
  'ht': { name: 'Haitian Creole', nativeName: 'Kreyòl ayisyen' },
  'hy': { name: 'Armenian', nativeName: 'Հայերեն' },
  'it': { name: 'Italian', nativeName: 'Italiano' },
  'ja': { name: 'Japanese', nativeName: '日本語' },
  'km': { name: 'Khmer', nativeName: 'ខ្មែរ' },
  'ko': { name: 'Korean', nativeName: '한국어' },
  'pa': { name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
  'pl': { name: 'Polish', nativeName: 'Polski' },
  'ru': { name: 'Russian', nativeName: 'Русский' },
  'th': { name: 'Thai', nativeName: 'ไทย' },
  'tl': { name: 'Tagalog', nativeName: 'Tagalog' },
  'vi': { name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  'yi': { name: 'Yiddish', nativeName: 'ייִדיש' },
  'zh': { name: 'Chinese (Traditional)', nativeName: '中文 (繁體)' },
  'zh-Hans': { name: 'Chinese (Simplified)', nativeName: '中文 (简体)' },
};

// Branding presets from this page's own URL, so a tester can share a link like
// /testing?consumer=mgm&vex_7983_fragno_logo_enabled=true.
// Testing page only: in production these come from init(), via IDME_INIT.
function readBrandingPreset() {
  const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  // Same rule as the iframe's ConsumerProvider: `?flag`, `?flag=true` and `?flag=1` are on.
  const flag = (name: string) => ["", "true", "1"].includes(params.get(name) ?? "false");
  return {
    consumer: getValidConsumer(params.get("consumer")) ?? ("" as const),
    fragnoLogoEnabled: flag("vex_7983_fragno_logo_enabled"),
  };
}

export function TestingPage() {
  const [sessionToken, setSessionToken] = useState("");
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [mockSdkLoaded, setMockSdkLoaded] = useState(false);
  const [mockLegalIdSdkLoaded, setMockLegalIdSdkLoaded] = useState(false);
  const [mockAddressSdkLoaded, setMockAddressSdkLoaded] = useState(false);
  const [workflowPhoneSdkLoaded, setWorkflowPhoneSdkLoaded] = useState(false);
  const [consentSdkLoaded, setConsentSdkLoaded] = useState(false);
  const [ssnCardSdkLoaded, setSsnCardSdkLoaded] = useState(false);
  const [ssnCardMockSdkLoaded, setSsnCardMockSdkLoaded] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState<SupportedLocale>("en");
  const [brandingPreset] = useState(readBrandingPreset);
  const [selectedConsumer, setSelectedConsumer] = useState<SupportedConsumer | "">(brandingPreset.consumer);
  // Fragno logo (VEX-7983). In production IVA passes it via init({ fragnoLogoEnabled })
  // from the `vex_7983_fragno_logo_enabled` Flipper flag; here it is a toggle so both
  // logo states can be exercised. Passed to every SDK's init() below, with the
  // consumer (which only the Legal ID and SSN card SDKs use, on the iframe URL).
  const [fragnoLogoEnabled, setFragnoLogoEnabled] = useState(brandingPreset.fragnoLogoEnabled);
  const branding = { consumer: selectedConsumer || undefined, fragnoLogoEnabled };
  const [consentEnabled, setConsentEnabled] = useState(true);
  // Which SSN card capture arm to exercise. In production this comes from IVA via
  // init({ manualCaptureEnabled }); here it is a toggle so both can be tried.
  const [manualCaptureEnabled, setManualCaptureEnabled] = useState(false);
  // Whether the SSN card flow shows the post-capture confirmation screen (VEX-7846).
  // In production this comes from IVA via init({ confirmationScreenEnabled }); here
  // it is a toggle so both (real and mock) SDKs can be exercised with it on or off.
  const [confirmationScreenEnabled, setConfirmationScreenEnabled] = useState(false);
  // Legal ID provider chooser (testing only — prod/IVA selects via a config flag, not a chooser).
  const [legalIdProvider, setLegalIdProvider] = useState<LegalIdProvider>(LEGAL_ID_PROVIDERS.veriff);
  // Hardcoded mock WE response for Legal ID mock testing.
  // Same real SDK is used — the mock Veriff URL points to provider-mock.idme.test
  // which simulates the Veriff flow without real document capture.
  const MOCK_WE_LEGAL_ID_RESPONSE = {
    data: {
      id: "9799daf9-04fe-4c30-a665-e28915f55bce",
      workflowId: 3,
      status: "WAITING_FOR_INPUT",
      ux: {
        screen: "LegalIdSdkCapture",
        inspectionName: "legal_id_verification",
        data: {
          link: "https://provider-mock.idme.test/veriff/6ea0b0e3-bab8-45e3-886d-6feb429d707c",
          inspectionId: "019eb4c2-3526-88d9-ad26-34971741a2fb",
        },
      },
    },
  };

  useEffect(() => {
    // Check if SDK is available (loaded via script tag in index.html or dynamically)
    const checkSDK = () => {
      if ((window as any).IDmeWebSDK) {
        console.log("[Testing] SDK detected:", (window as any).IDmeWebSDK);
        setSdkLoaded(true);

        // Initialize SDK
        (window as any).IDmeWebSDK.init({
          // env: "labs",
          locale: selectedLocale,
          ...branding,
          consent_enabled: consentEnabled,
          // Analytics: on here (unlike IVA, where it's a rollout flag) — this page exists to
          // exercise the SDK, and the analytics path is only observable when it's emitting.
          analyticsEnabled: true,
        });
      } else {
        console.log("[Testing] SDK not yet loaded, will load from src/sdk/index.ts");
        // In dev mode, we'll import the SDK directly
        loadSDKDynamically();
      }
    };

    const loadSDKDynamically = async () => {
      try {
        // Import SDK directly in development mode
        const { IDmeWebSDK } = await import("../sdk/index");
        (window as any).IDmeWebSDK = IDmeWebSDK;

        IDmeWebSDK.init({
          // env: "labs",
          locale: selectedLocale,
          ...branding,
          consent_enabled: consentEnabled,
          // Analytics: on here (unlike IVA, where it's a rollout flag) — this page exists to
          // exercise the SDK, and the analytics path is only observable when it's emitting.
          analyticsEnabled: true,
        });

        setSdkLoaded(true);
        console.log("[Testing] SDK loaded dynamically");
      } catch (err) {
        console.error("[Testing] Failed to load SDK:", err);
      }
    };

    checkSDK();
  }, [selectedLocale, selectedConsumer, fragnoLogoEnabled, consentEnabled]);

  useEffect(() => {
    const loadMockSDK = async () => {
      try {
        const { IDmeMockFaceLivenessSDK } = await import("../sdk/faceVerificationMock");
        (window as any).IDmeMockFaceLivenessSDK = IDmeMockFaceLivenessSDK;

        IDmeMockFaceLivenessSDK.init({
          locale: selectedLocale,
          ...branding,
        });

        setMockSdkLoaded(true);
        console.log("[Testing] Mock SDK loaded dynamically");
      } catch (err) {
        console.error("[Testing] Failed to load Mock SDK:", err);
      }
    };

    loadMockSDK();
  }, [selectedLocale, selectedConsumer, fragnoLogoEnabled]);

  useEffect(() => {
    const loadMockLegalIdSDK = async () => {
      try {
        const { IDmeMockLegalIdVerificationSDK } = await import("../sdk/legalIdVerificationMock");
        (window as any).IDmeMockLegalIdVerificationSDK = IDmeMockLegalIdVerificationSDK;

        IDmeMockLegalIdVerificationSDK.init({
          locale: selectedLocale,
          ...branding,
        });

        setMockLegalIdSdkLoaded(true);
        console.log("[Testing] Mock Legal ID SDK loaded dynamically");
      } catch (err) {
        console.error("[Testing] Failed to load Mock Legal ID SDK:", err);
      }
    };

    loadMockLegalIdSDK();
  }, [selectedLocale, selectedConsumer, fragnoLogoEnabled]);

  useEffect(() => {
    const loadMockAddressSDK = async () => {
      try {
        const { IDmeMockAddressHistorySDK } = await import("../sdk/addressHistoryMock");
        (window as any).IDmeMockAddressHistorySDK = IDmeMockAddressHistorySDK;

        IDmeMockAddressHistorySDK.init({
          locale: selectedLocale,
          ...branding,
        });

        setMockAddressSdkLoaded(true);
        console.log("[Testing] Mock Address SDK loaded dynamically");
      } catch (err) {
        console.error("[Testing] Failed to load Mock Address SDK:", err);
      }
    };

    loadMockAddressSDK();
  }, [selectedLocale, selectedConsumer, fragnoLogoEnabled]);

  useEffect(() => {
    const loadWorkflowPhoneSDK = async () => {
      try {
        const { IDmeWorkflowPhoneSDK } = await import("../sdk/workflowPhoneSdk");
        (window as any).IDmeWorkflowPhoneSDK = IDmeWorkflowPhoneSDK;

        IDmeWorkflowPhoneSDK.init({ ...branding });

        setWorkflowPhoneSdkLoaded(true);
        console.log("[Testing] Workflow Phone SDK loaded dynamically");
      } catch (err) {
        console.error("[Testing] Failed to load Workflow Phone SDK:", err);
      }
    };

    loadWorkflowPhoneSDK();
  }, [selectedConsumer, fragnoLogoEnabled]);

  useEffect(() => {
    const loadConsentSDK = async () => {
      try {
        const { IDmeWorkflowConsentSDK } = await import("../sdk/workflowConsentSdk");
        (window as any).IDmeWorkflowConsentSDK = IDmeWorkflowConsentSDK;

        IDmeWorkflowConsentSDK.init({ locale: selectedLocale, ...branding });

        setConsentSdkLoaded(true);
        console.log("[Testing] Workflow Consent SDK loaded dynamically");
      } catch (err) {
        console.error("[Testing] Failed to load Workflow Consent SDK:", err);
      }
    };

    loadConsentSDK();
  }, [selectedLocale, selectedConsumer, fragnoLogoEnabled]);

  useEffect(() => {
    const loadSsnCardSDK = async () => {
      try {
        const { IDmeSsnCardVerificationSDK } = await import("../sdk/ssnCardVerification");
        (window as any).IDmeSsnCardVerificationSDK = IDmeSsnCardVerificationSDK;

        IDmeSsnCardVerificationSDK.init({
          locale: selectedLocale,
          ...branding,
          // On unconditionally here, unlike IVA where it's a rollout flag: this page
          // exists to exercise the SDK, and the analytics path is only observable
          // when it's emitting.
          analyticsEnabled: true,
          manualCaptureEnabled,
          confirmationScreenEnabled,
        });

        setSsnCardSdkLoaded(true);
        console.log("[Testing] SSN Card SDK loaded dynamically");
      } catch (err) {
        console.error("[Testing] Failed to load SSN Card SDK:", err);
      }
    };

    loadSsnCardSDK();
    // manualCaptureEnabled/confirmationScreenEnabled are deps because both flags
    // are read at init(), not at start — without them the toggles would only
    // take effect on a page reload.
  }, [selectedLocale, selectedConsumer, fragnoLogoEnabled, manualCaptureEnabled, confirmationScreenEnabled]);

  useEffect(() => {
    const loadSsnCardMockSDK = async () => {
      try {
        const { IDmeMockSsnCardVerificationSDK } = await import("../sdk/ssnCardVerificationMock");
        (window as any).IDmeMockSsnCardVerificationSDK = IDmeMockSsnCardVerificationSDK;

        IDmeMockSsnCardVerificationSDK.init({
          locale: selectedLocale,
          ...branding,
          confirmationScreenEnabled,
        });

        setSsnCardMockSdkLoaded(true);
        console.log("[Testing] Mock SSN Card SDK loaded dynamically");
      } catch (err) {
        console.error("[Testing] Failed to load Mock SSN Card SDK:", err);
      }
    };

    loadSsnCardMockSDK();
    // confirmationScreenEnabled is a dep for the same reason as the real SDK's effect above.
  }, [selectedLocale, selectedConsumer, fragnoLogoEnabled, confirmationScreenEnabled]);

  const handleStartFaceVerification = () => {
    if (!sdkLoaded) {
      alert("SDK is not loaded yet!");
      return;
    }

    if (!sessionToken || sessionToken.trim() === "") {
      alert("Please enter a session token first!");
      return;
    }

    setResult(null);
    setError(null);

    const IDmeWebSDK = (window as any).IDmeWebSDK;

    // Generate a mock externalReferenceId (32 hex chars, similar to UUID format)
    const mockExternalReferenceId = Array.from({ length: 32 }, () =>
      Math.floor(Math.random() * 16).toString(16)
    ).join("");

    console.log("[Testing] Starting face verification with token:", sessionToken);
    console.log("[Testing] Mock externalReferenceId:", mockExternalReferenceId);

    IDmeWebSDK.startFaceLiveness({
      sessionToken: sessionToken,
      externalReferenceId: mockExternalReferenceId,
      onComplete: (completionResult: any) => {
        console.log("[Testing] Face verification complete:", completionResult);
        setResult(completionResult);
      },
      onError: (errorResult: any) => {
        console.error("[Testing] Face verification error:", errorResult);
        setError(errorResult);
      },
    });
  };

  const handleStartMockLegalIdVerification = () => {
    if (!mockLegalIdSdkLoaded) {
      alert("Mock Legal ID SDK is not loaded yet!");
      return;
    }

    setResult(null);
    setError(null);

    const mockSDK = (window as any).IDmeMockLegalIdVerificationSDK;

    console.log("[Testing] Starting mock legal ID verification");

    mockSDK.startLegalIdVerification({
      sessionToken: sessionToken || "mock-session-token",
      onComplete: (completionResult: any) => {
        console.log("[Testing] Mock legal ID verification complete:", completionResult);
        setResult(completionResult);
      },
      onError: (errorResult: any) => {
        console.error("[Testing] Mock legal ID verification error:", errorResult);
        setError(errorResult);
      },
    });
  };

  const handleStartMockSsnCardVerification = () => {
    if (!ssnCardMockSdkLoaded) {
      alert("Mock SSN Card SDK is not loaded yet!");
      return;
    }

    setResult(null);
    setError(null);

    const mockSDK = (window as any).IDmeMockSsnCardVerificationSDK;

    console.log("[Testing] Starting mock SSN card verification");

    mockSDK.startSsnCardVerification({
      sessionToken: sessionToken || "mock-session-token",
      onComplete: (completionResult: any) => {
        console.log("[Testing] Mock SSN card verification complete:", completionResult);
        setResult(completionResult);
      },
      onError: (errorResult: any) => {
        console.error("[Testing] Mock SSN card verification error:", errorResult);
        setError(errorResult);
      },
    });
  };

  const handleStartMockFaceVerification = () => {
    if (!mockSdkLoaded) {
      alert("Mock SDK is not loaded yet!");
      return;
    }

    setResult(null);
    setError(null);

    const mockSDK = (window as any).IDmeMockFaceLivenessSDK;

    console.log("[Testing] Starting mock face verification");

    mockSDK.startFaceLiveness({
      sessionToken: sessionToken || "mock-session-token",
      onComplete: (completionResult: any) => {
        console.log("[Testing] Mock face verification complete:", completionResult);
        setResult(completionResult);
      },
      onError: (errorResult: any) => {
        console.error("[Testing] Mock face verification error:", errorResult);
        setError(errorResult);
      },
    });
  };

  const handleStartLegalIdVerification = async () => {
    setResult(null);
    setError(null);

    // Both providers require a session token to start (Regula POSTs the captured images to athena;
    // Veriff creates a session). Guard once up front.
    if (!sessionToken || sessionToken.trim() === "") {
      alert("Please enter a session token first!");
      return;
    }

    if (!sdkLoaded) {
      alert("SDK is not loaded yet!");
      return;
    }

    const IDmeWebSDK = (window as any).IDmeWebSDK;

    console.log("[Testing] Starting legal ID verification, provider:", legalIdProvider);

    IDmeWebSDK.startLegalIdVerification({
      provider: legalIdProvider,
      sessionToken: sessionToken,
      onComplete: (completionResult: any) => {
        console.log("[Testing] Document verification complete, provider:", legalIdProvider);
        setResult(completionResult);
      },
      onError: (errorResult: any) => {
        console.error("[Testing] Document verification error:", errorResult?.errorCode);
        setError(errorResult);
      },
    });
  };

  const handleStartSsnCardVerification = () => {
    if (!ssnCardSdkLoaded) {
      alert("SSN Card SDK is not loaded yet!");
      return;
    }

    if (!sessionToken || sessionToken.trim() === "") {
      alert("Please enter a session token first!");
      return;
    }

    setResult(null);
    setError(null);

    const sdk = (window as any).IDmeSsnCardVerificationSDK;

    // Generate a mock externalReferenceId (32 hex chars) so the flow has a client id
    // to pass through — this becomes analytics `referenceId` (mirrors face verification).
    const mockExternalReferenceId = Array.from({ length: 32 }, () =>
      Math.floor(Math.random() * 16).toString(16)
    ).join("");

    console.log("[Testing] Starting SSN card verification with token:", sessionToken);
    console.log("[Testing] Mock externalReferenceId:", mockExternalReferenceId);

    sdk.startSsnCardVerification({
      sessionToken: sessionToken,
      externalReferenceId: mockExternalReferenceId,
      onComplete: (completionResult: any) => {
        console.log("[Testing] SSN card verification complete");
        setResult(completionResult);
      },
      onError: (errorResult: any) => {
        console.error("[Testing] SSN card verification error:", errorResult);
        setError(errorResult);
      },
    });
  };

  const handleStartAddressHistory = () => {
    if (!sdkLoaded) {
      alert("SDK is not loaded yet!");
      return;
    }

    if (!sessionToken || sessionToken.trim() === "") {
      alert("Please enter a session token first!");
      return;
    }

    setResult(null);
    setError(null);

    const IDmeWebSDK = (window as any).IDmeWebSDK;

    console.log("[Testing] Starting address history with token:", sessionToken);

    IDmeWebSDK.startAddressHistory({
      sessionToken: sessionToken,
      onComplete: (completionResult: any) => {
        console.log("[Testing] Address verification complete:", completionResult);
        setResult(completionResult);
      },
      onError: (errorResult: any) => {
        console.error("[Testing] Address verification error:", errorResult);
        setError(errorResult);
      },
    });
  };

  const handleStartMockAddressHistory = () => {
    if (!mockAddressSdkLoaded) {
      alert("Mock Address SDK is not loaded yet!");
      return;
    }

    setResult(null);
    setError(null);

    const mockSDK = (window as any).IDmeMockAddressHistorySDK;

    console.log("[Testing] Starting mock address history");

    mockSDK.startAddressHistory({
      sessionToken: sessionToken || "mock-session-token",
      onComplete: (completionResult: any) => {
        console.log("[Testing] Mock address history complete:", completionResult);
        setResult(completionResult);
      },
      onError: (errorResult: any) => {
        console.error("[Testing] Mock address history error:", errorResult);
        setError(errorResult);
      },
    });
  };

  const handleStartWorkflowPhone = () => {
    if (!workflowPhoneSdkLoaded) {
      alert("Workflow Phone SDK is not loaded yet!");
      return;
    }

    setResult(null);
    setError(null);

    const sdk = (window as any).IDmeWorkflowPhoneSDK;

    console.log("[Testing] Starting workflow phone with mock WE data");

    sdk.startWorkflowPhone({
      executionId: `mock-exec-${Date.now()}`,
      workflowId: 17,
      inspectionName: "phone_capture",
      // No authToken / apiBaseUrl — submit will fail (API not ready yet), UI can be tested
      onComplete: (completionResult: any) => {
        console.log("[Testing] Workflow phone complete:", completionResult);
        setResult(completionResult);
      },
      onError: (errorResult: any) => {
        console.error("[Testing] Workflow phone error:", errorResult);
        setError(errorResult);
      },
    });
  };

  const handleStartWorkflowLegalIdMock = async () => {
    setResult(null);
    setError(null);

    const uxData = MOCK_WE_LEGAL_ID_RESPONSE.data.ux.data;
    const { link, inspectionId } = uxData;
    const { id: executionId, workflowId } = MOCK_WE_LEGAL_ID_RESPONSE.data;

    // Load the real Workflow Legal ID SDK dynamically if not already available
    let sdk = (window as any).IDmeWorkflowLegalIdSDK;
    if (!sdk) {
      try {
        const { IDmeWorkflowLegalIdSDK } = await import("../sdk/workflowLegalIdSdk");
        (window as any).IDmeWorkflowLegalIdSDK = IDmeWorkflowLegalIdSDK;
        sdk = IDmeWorkflowLegalIdSDK;
        console.log("[Testing] Workflow Legal ID SDK loaded dynamically");
      } catch (err) {
        console.error("[Testing] Failed to load Workflow Legal ID SDK:", err);
        setError({ errorCode: "sdk_load_failed", message: "Failed to load Workflow Legal ID SDK" });
        return;
      }
    }

    // Branding is read at start(), so re-init with the page's current toggles each launch.
    sdk.init({ locale: selectedLocale, ...branding });

    console.log("[Testing] Starting Workflow Legal ID (mock) — link:", link, "inspectionId:", inspectionId);

    sdk.startWorkflowLegalId({
      veriffUrl: link,
      executionId,
      workflowId,
      inspectionName: "legal_id_verification",
      apiBaseUrl: `${window.location.origin}/api/workflow`,
      onComplete: (completionResult: any) => {
        console.log("[Testing] Workflow Legal ID mock complete:", completionResult);
        setResult(completionResult);
      },
      onError: (errorResult: any) => {
        console.error("[Testing] Workflow Legal ID mock error:", errorResult);
        setError(errorResult);
      },
    });
  };

  const handleStartWorkflowConsent = () => {
    if (!consentSdkLoaded) {
      alert("Workflow Consent SDK is not loaded yet!");
      return;
    }

    setResult(null);
    setError(null);

    const sdk = (window as any).IDmeWorkflowConsentSDK;

    console.log("[Testing] Starting workflow consent flow");

    sdk.startWorkflowConsent({
      partnerName: "IDMe",
      onAllow: (result: any) => {
        console.log("[Testing] Consent allowed:", result);
        setResult(result);
      },
      onDecline: () => {
        console.log("[Testing] Consent declined");
        setError({ errorCode: "user_declined", message: "User declined consent" });
        setResult(null);
      },
      onError: (errorResult: any) => {
        console.error("[Testing] Consent error:", errorResult);
        setError(errorResult);
      },
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.title}>Customer Integration Test</h1>
          <p style={styles.subtitle}>
            This page simulates how a customer would integrate the IDMe Web SDK
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <div style={styles.badge}>
              SDK: {sdkLoaded ? "✅ Loaded" : "⏳ Loading..."}
            </div>
            <div style={{ ...styles.badge, backgroundColor: "#fff3e0", color: "#e65100" }}>
              Mock Face SDK: {mockSdkLoaded ? "✅ Loaded" : "⏳ Loading..."}
            </div>
            <div style={{ ...styles.badge, backgroundColor: "#f3e5f5", color: "#6a1b9a" }}>
              Mock Legal ID SDK: {mockLegalIdSdkLoaded ? "✅ Loaded" : "⏳ Loading..."}
            </div>
            <div style={{ ...styles.badge, backgroundColor: "#e8f5e9", color: "#2e7d32" }}>
              Mock Address SDK: {mockAddressSdkLoaded ? "✅ Loaded" : "⏳ Loading..."}
            </div>
            <div style={{ ...styles.badge, backgroundColor: "#e0f7f4", color: "#00695c" }}>
              Workflow Phone SDK: {workflowPhoneSdkLoaded ? "✅ Loaded" : "⏳ Loading..."}
            </div>
            <div style={{ ...styles.badge, backgroundColor: "#fce4ec", color: "#c2185b" }}>
              Consent SDK: {consentSdkLoaded ? "✅ Loaded" : "⏳ Loading..."}
            </div>
            <div style={{ ...styles.badge, backgroundColor: "#ede7f6", color: "#4527a0" }}>
              SSN Card SDK: {ssnCardSdkLoaded ? "✅ Loaded" : "⏳ Loading..."}
            </div>
            <div style={{ ...styles.badge, backgroundColor: "#ede7f6", color: "#4527a0" }}>
              Mock SSN Card SDK: {ssnCardMockSdkLoaded ? "✅ Loaded" : "⏳ Loading..."}
            </div>
          </div>
        </div>

        <TokenManager onTokenSet={setSessionToken} currentToken={sessionToken} />

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Start Verification</h2>
          <div style={styles.buttonGroup}>
            <button
              onClick={handleStartFaceVerification}
              style={styles.primaryButton}
              disabled={!sdkLoaded}
            >
              Start Face Verification
            </button>
            <div style={styles.legalIdSplitButton}>
              <span
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!(legalIdProvider === LEGAL_ID_PROVIDERS.veriff && !sdkLoaded)) handleStartLegalIdVerification();
                }}
                style={{
                  ...styles.legalIdStartLabel,
                  ...(legalIdProvider === LEGAL_ID_PROVIDERS.veriff && !sdkLoaded ? styles.legalIdStartLabelDisabled : {}),
                }}
              >
                Start Legal ID Verification
              </span>
              <select
                value={legalIdProvider}
                onChange={(e) => setLegalIdProvider(e.target.value as LegalIdProvider)}
                onClick={(e) => e.stopPropagation()}
                aria-label="Legal ID provider"
                title="Choose provider"
                style={styles.legalIdInlineSelect}
              >
                {Object.values(LEGAL_ID_PROVIDERS).map((p) => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleStartAddressHistory}
              style={styles.secondaryButton}
              disabled={!sdkLoaded}
            >
              Start Address History
            </button>
            <button
              onClick={handleStartSsnCardVerification}
              style={styles.secondaryButton}
              disabled={!ssnCardSdkLoaded}
            >
              Start SSN Card Verification
            </button>
          </div>
          <div style={{ ...styles.buttonGroup, marginTop: "12px" }}>
            <button
              onClick={handleStartMockFaceVerification}
              style={styles.mockButton}
              disabled={!mockSdkLoaded}
              title="No session token required — uses mock flow"
            >
              Start Mock Face Verification
            </button>
            <button
              onClick={handleStartMockLegalIdVerification}
              style={styles.mockLegalIdButton}
              disabled={!mockLegalIdSdkLoaded}
              title="No session token required — uses mock flow"
            >
              Start Mock Legal ID Verification
            </button>
            <button
              onClick={handleStartMockAddressHistory}
              style={styles.mockAddressButton}
              disabled={!mockAddressSdkLoaded}
              title="No session token required — uses mock flow"
            >
              Start Mock Address History
            </button>
            <button
              onClick={handleStartMockSsnCardVerification}
              style={styles.mockSsnCardButton}
              disabled={!ssnCardMockSdkLoaded}
              title="No session token required — uses mock flow"
            >
              Start Mock SSN Card Verification
            </button>
            <button
              onClick={handleStartWorkflowPhone}
              style={styles.workflowPhoneButton}
              disabled={!workflowPhoneSdkLoaded}
              title="No session token required — opens phone entry screen (submit will fail until API is ready)"
            >
              Start Workflow Phone (Mock WE)
            </button>
            <button
              onClick={handleStartWorkflowConsent}
              style={styles.workflowConsentButton}
              disabled={!consentSdkLoaded}
              title="No session token required — opens consent flow"
            >
              Start Consent Flow
            </button>
            <button
              onClick={handleStartWorkflowLegalIdMock}
              style={styles.workflowLegalIdCdtButton}
              title="Uses mock Veriff URL (provider-mock.idme.test) with the real Workflow Legal ID SDK"
            >
              Start WF Legal ID (Mock)
            </button>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Select Language</h2>
          <div style={styles.localeSelector}>
            <label htmlFor="locale-select" style={styles.label}>
              Locale:
            </label>
            <select
              id="locale-select"
              value={selectedLocale}
              onChange={(e) => setSelectedLocale(e.target.value as SupportedLocale)}
              style={styles.select}
            >
              {SUPPORTED_LOCALES.map((locale) => (
                <option key={locale} value={locale}>
                  {LANGUAGE_NAMES[locale]?.nativeName || locale} ({locale})
                </option>
              ))}
            </select>
            <p style={styles.helperText}>
              Face verification: <code style={styles.inlineCode}>/{selectedLocale}/face-liveness</code>
              {" · "}
              Mock: <code style={styles.inlineCode}>/{selectedLocale}/face-liveness-mock</code>
              <br />
              Legal ID: <code style={styles.inlineCode}>/{selectedLocale}/legal-id-verification</code>
              {" · "}
              Mock: <code style={styles.inlineCode}>/{selectedLocale}/legal-id-verification-mock</code>
              <br />
              Address: <code style={styles.inlineCode}>/{selectedLocale}/address-history</code>
              {" · "}
              Mock: <code style={styles.inlineCode}>/{selectedLocale}/address-history-mock</code>
            </p>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>SDK Options</h2>

          <div style={{ ...styles.localeSelector, marginBottom: '20px' }}>
            <label htmlFor="consumer-select" style={styles.label}>
              Consumer:
            </label>
            <select
              id="consumer-select"
              value={selectedConsumer}
              onChange={(e) => setSelectedConsumer(e.target.value as SupportedConsumer | "")}
              style={styles.select}
            >
              <option value="">Default (no consumer)</option>
              {SUPPORTED_CONSUMERS.map((consumer) => (
                <option key={consumer} value={consumer}>
                  {consumer}
                </option>
              ))}
            </select>
            <p style={styles.helperText}>
              Consumer theme and partner logo, passed to every SDK's <code style={styles.inlineCode}>init()</code>.
            </p>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={consentEnabled}
              onChange={(e) => setConsentEnabled(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={styles.label}>
              Enable Consent (<code style={styles.inlineCode}>consent_enabled</code>)
            </span>
          </label>
          <p style={styles.helperText}>
            When checked, the consent text and biometric policy link are shown before opening the camera.
          </p>

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginTop: '20px' }}>
            <input
              type="checkbox"
              checked={manualCaptureEnabled}
              onChange={(e) => setManualCaptureEnabled(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={styles.label}>
              SSN Card manual capture (<code style={styles.inlineCode}>manualCaptureEnabled</code>)
            </span>
          </label>
          <p style={styles.helperText}>
            Off = auto capture (today's default): Regula detects both sides itself.
            On = the member presses a shutter for each side, front then back, with
            retakes on a failed quality check. Re-initialises the SSN Card SDK, so
            toggle it before starting a verification.
          </p>

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginTop: '20px' }}>
            <input
              type="checkbox"
              checked={confirmationScreenEnabled}
              onChange={(e) => setConfirmationScreenEnabled(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={styles.label}>
              SSN Card confirmation screen (<code style={styles.inlineCode}>confirmationScreenEnabled</code>)
            </span>
          </label>
          <p style={styles.helperText}>
            Off (today's default) = a passed outcome completes immediately. On = the
            member sees the extracted name/SSN on a confirmation screen and can
            Confirm or Retake before completing. Applies to both the real and mock
            SSN Card SDKs below. Re-initialises the SDKs, so toggle it before
            starting a verification.
          </p>

          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginTop: '20px' }}>
            <input
              type="checkbox"
              checked={fragnoLogoEnabled}
              onChange={(e) => setFragnoLogoEnabled(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={styles.label}>
              Fragno logo (<code style={styles.inlineCode}>vex_7983_fragno_logo_enabled</code>)
            </span>
          </label>
          <p style={styles.helperText}>
            Off (today's default) = each screen's current logo. On = the same logo
            choice, with the IDMe logo drawn by the Fragno logo lockup. Sent as{' '}
            <code style={styles.inlineCode}>fragnoLogoEnabled</code>. Re-initialises the
            SDKs, so toggle it before starting a verification. Preset with{' '}
            <code style={styles.inlineCode}>?consumer=mgm&amp;vex_7983_fragno_logo_enabled=true</code>.
          </p>
        </div>

        {result && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>✅ Verification Success</h2>
            <div style={styles.resultBox}>
              <pre style={styles.code}>{JSON.stringify(result, null, 2)}</pre>
            </div>
          </div>
        )}

        {error && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>❌ Verification Error</h2>
            <div style={styles.errorBox}>
              <pre style={styles.code}>{JSON.stringify(error, null, 2)}</pre>
            </div>
          </div>
        )}

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Integration Code</h2>
          <div style={styles.codeBlock}>
            <pre style={styles.code}>{`<!-- Include SDK in your HTML -->
<script src="https://cdn.id.me/websdk.js"></script>

<script>
  // Initialize SDK
  IDmeWebSDK.init({
    env: 'production',
    clientId: 'your-client-id',
    locale: '${selectedLocale}', // Optional: 'en', 'es', 'fr-CA', 'ja', etc. Defaults to 'en'
    consent_enabled: ${consentEnabled}, // Optional: show consent text before camera. Defaults to false
    consumer: ${selectedConsumer ? `'${selectedConsumer}'` : "undefined"}, // Optional: consumer theme and partner logo
    fragnoLogoEnabled: ${fragnoLogoEnabled}, // Optional: Fragno logo lockup (vex_7983_fragno_logo_enabled). Defaults to false
  });

  // Start face verification
  IDmeWebSDK.startFaceLiveness({
    sessionToken: 'your-session-token-here',
    externalReferenceId: 'your-reference-id', // Optional: for tracking
    onComplete: (result) => {
      console.log('Success:', result);
      // Handle success (e.g., redirect to success page)
    },
    onError: (error) => {
      console.error('Error:', error);
      // Handle error (e.g., show error message)
    }
  });
</script>`}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f8f9fa",
    padding: "40px 20px",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  content: {
    maxWidth: "900px",
    margin: "0 auto",
  },
  header: {
    textAlign: "center",
    marginBottom: "40px",
  },
  title: {
    fontSize: "36px",
    fontWeight: "700",
    color: "#1a1a1a",
    marginBottom: "12px",
  },
  subtitle: {
    fontSize: "18px",
    color: "#666",
    marginBottom: "20px",
  },
  badge: {
    display: "inline-block",
    padding: "8px 16px",
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    borderRadius: "20px",
    fontSize: "14px",
    fontWeight: "600",
  },
  section: {
    backgroundColor: "white",
    padding: "32px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    marginBottom: "24px",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#1a1a1a",
    marginBottom: "16px",
  },
  buttonGroup: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  primaryButton: {
    flex: 1,
    minWidth: "160px",
    padding: "16px 32px",
    fontSize: "16px",
    fontWeight: "600",
    color: "white",
    backgroundColor: "#0066cc",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  mockButton: {
    flex: 1,
    minWidth: "160px",
    padding: "16px 32px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#e65100",
    backgroundColor: "#fff3e0",
    border: "2px solid #e65100",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  mockLegalIdButton: {
    flex: 1,
    minWidth: "200px",
    padding: "16px 32px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#6a1b9a",
    backgroundColor: "#f3e5f5",
    border: "2px solid #6a1b9a",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  legalIdSplitButton: {
    minWidth: "160px",
    display: "flex",
    alignItems: "stretch",
    border: "2px solid #0066cc",
    borderRadius: "8px",
    backgroundColor: "white",
    overflow: "hidden",
  },
  legalIdStartLabel: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px 20px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#0066cc",
    cursor: "pointer",
    userSelect: "none",
  },
  legalIdStartLabelDisabled: {
    color: "#9bbfe0",
    cursor: "not-allowed",
  },
  legalIdInlineSelect: {
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    border: "none",
    borderLeft: "2px solid #0066cc",
    backgroundColor: "#f0f7ff",
    color: "#0066cc",
    fontSize: "14px",
    fontWeight: "600",
    textAlign: "center",
    textAlignLast: "center",
    paddingLeft: "16px",
    paddingRight: "26px",
    cursor: "pointer",
    outline: "none",
    // Custom caret placed close to the (centered) text, instead of the native far-right arrow.
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%230066cc' stroke-width='1.5' fill='none'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 9px center",
  },
  mockAddressButton: {
    flex: 1,
    minWidth: "200px",
    padding: "16px 32px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#2e7d32",
    backgroundColor: "#e8f5e9",
    border: "2px solid #2e7d32",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  mockSsnCardButton: {
    flex: 1,
    minWidth: "200px",
    padding: "16px 32px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#4527a0",
    backgroundColor: "#ede7f6",
    border: "2px solid #4527a0",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  workflowPhoneButton: {
    flex: 1,
    minWidth: "200px",
    padding: "16px 32px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#00695c",
    backgroundColor: "#e0f7f4",
    border: "2px solid #00695c",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  workflowConsentButton: {
    flex: 1,
    minWidth: "200px",
    padding: "16px 32px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#c2185b",
    backgroundColor: "#fce4ec",
    border: "2px solid #c2185b",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  workflowLegalIdCdtButton: {
    flex: 1,
    minWidth: "200px",
    padding: "16px 32px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#283593",
    backgroundColor: "#e8eaf6",
    border: "2px solid #283593",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  secondaryButton: {
    flex: 1,
    minWidth: "200px",
    padding: "16px 32px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#0066cc",
    backgroundColor: "white",
    border: "2px solid #0066cc",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  resultBox: {
    backgroundColor: "#d4edda",
    border: "1px solid #c3e6cb",
    borderRadius: "8px",
    padding: "20px",
  },
  errorBox: {
    backgroundColor: "#f8d7da",
    border: "1px solid #f5c6cb",
    borderRadius: "8px",
    padding: "20px",
  },
  codeBlock: {
    backgroundColor: "#f5f5f5",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "20px",
    overflow: "auto",
  },
  code: {
    margin: 0,
    fontSize: "14px",
    fontFamily: '"Monaco", "Menlo", "Courier New", monospace',
    lineHeight: "1.6",
    color: "#333",
  },
  localeSelector: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
  },
  select: {
    padding: "12px",
    fontSize: "16px",
    border: "2px solid #e0e0e0",
    borderRadius: "8px",
    backgroundColor: "white",
    cursor: "pointer",
    outline: "none",
    transition: "border-color 0.2s",
  },
  helperText: {
    fontSize: "14px",
    color: "#666",
    margin: "4px 0 0 0",
  },
  inlineCode: {
    backgroundColor: "#f5f5f5",
    padding: "2px 6px",
    borderRadius: "4px",
    fontFamily: '"Monaco", "Menlo", "Courier New", monospace',
    fontSize: "13px",
    color: "#0066cc",
  },
};
